import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ApplicantList from "@/components/applicants/ApplicantList";
import ApplicantPipeline from "@/components/applicants/ApplicantPipeline";
import RejectedApplicantsList from "@/components/applicants/RejectedApplicantsList";
import LandingPageSettings from "@/components/applicants/LandingPageSettings";
import AddApplicantDialog from "@/components/applicants/AddApplicantDialog";
import CreateApplicantFromDocumentsDialog from "@/components/applicants/CreateApplicantFromDocumentsDialog";
import type { Applicant } from "@/types/applicant";
import { Loader2, Users, Kanban, Settings2, ExternalLink, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const ApplicantManagementPage = () => {
  const { user, loading: authLoading, userRole } = useAuth();
  const navigate = useNavigate();
  const [applicants, setApplicants] = useState<Applicant[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pipeline");

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchApplicants();
    }
  }, [user]);

  const fetchApplicants = async () => {
    try {
      const { data, error } = await supabase
        .from("applicants")
        .select("*")
        .is("converted_to_employee_id", null)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setApplicants((data || []) as unknown as Applicant[]);
    } catch (error) {
      console.error("Error fetching applicants:", error);
    } finally {
      setLoading(false);
    }
  };

  const canManageLandingPage = userRole === "admin" || userRole === "hr_manager";

  // Count active (non-rejected) and rejected applicants
  const activeApplicants = applicants.filter(a => a.current_stage !== "rejected");
  const rejectedApplicants = applicants.filter(a => a.current_stage === "rejected");

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Bewerber Management</h1>
            <p className="text-sm text-muted-foreground">
              {activeApplicants.length} aktive Bewerber
              {rejectedApplicants.length > 0 && (
                <span className="text-destructive"> · {rejectedApplicants.length} abgesagt</span>
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <AddApplicantDialog onSuccess={fetchApplicants} />
            <CreateApplicantFromDocumentsDialog onSuccess={fetchApplicants} />
            <Button
              variant="outline"
              size="default"
              onClick={() => window.open("/apply", "_blank")}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Landing Page
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="pipeline" className="gap-2">
              <Kanban className="h-4 w-4" />
              <span className="hidden sm:inline">Pipeline</span>
            </TabsTrigger>
            <TabsTrigger value="list" className="gap-2">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Liste</span>
            </TabsTrigger>
            <TabsTrigger value="rejected" className="gap-2">
              <XCircle className="h-4 w-4" />
              <span className="hidden sm:inline">Abgesagt</span>
              {rejectedApplicants.length > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                  {rejectedApplicants.length}
                </Badge>
              )}
            </TabsTrigger>
            {canManageLandingPage && (
              <TabsTrigger value="settings" className="gap-2">
                <Settings2 className="h-4 w-4" />
                <span className="hidden sm:inline">Landing Page</span>
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="pipeline">
            <ApplicantPipeline applicants={activeApplicants} onUpdate={fetchApplicants} />
          </TabsContent>

          <TabsContent value="list">
            <ApplicantList applicants={activeApplicants} onUpdate={fetchApplicants} />
          </TabsContent>

          <TabsContent value="rejected">
            <RejectedApplicantsList applicants={applicants} onUpdate={fetchApplicants} />
          </TabsContent>

          {canManageLandingPage && (
            <TabsContent value="settings">
              <LandingPageSettings />
            </TabsContent>
          )}
        </Tabs>
      </div>
  );
};

export default ApplicantManagementPage;
